import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { getCR, saveCR, getDossier } from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import { saveDraft, loadDraft, removeDraft, saveDraftSync } from '../services/draftStorage';

const TYPES_RACCORDEMENT = ['RAC_I', 'RAC_C', 'RAC_F', 'RAC_P', 'PTO_C'];
// Ce motif n'est pas un échec technique : l'intervention est simplement
// déplacée. Il ouvre la saisie de la nouvelle date, que l'admin pourra
// appliquer d'un clic depuis le panneau.
const MOTIF_REPORT = 'Report RDV suite demande client ou GEM';
// « Technicien / VALEA » n'est pas un demandeur comme les autres : Covage
// compte ces reports dans un SLA à 0 % lorsqu'ils surviennent à moins de 48 h
// du rendez-vous. Sans ce choix, ils se confondraient avec un report client.
const DEMANDEURS_REPORT = ['Client', 'GEM', 'Technicien / VALEA'];

const TYPES_ECHEC = [
  'Client absent',
  'Problème d\'accès',
  'Cheminement introuvable',
  'Chambre télécom inaccessible (tampon scellé)',
  'Blocage GC (entre deux chambres)',
  'Intervention sur DEVIS',
  'RDVNH (rendez-vous non honoré faute technicien)',
  'Client non éligible FTTH',
  'Commande annulé par le client',
  'Chambre sur voirie besoin ARC',
  'Pas de continuité PB PM',
  'Pas de signal PM',
  'PBO Saturé',
  'Erreur d\'adresse',
  'Travaux à réaliser par le client',
  'Report RDV suite demande client ou GEM',
  'Indisponibilité technicien ou binôme',
  'Fourreau bouché / hydrocurage nécessaire',
  'Autorisation propriétaire ou syndic',
  'Autre',
];

// Un motif déjà présent dans la liste saisi sous « Autre » échappe aux
// statistiques d'échec. On le détecte pour inviter à le sélectionner.
const sansAccents = s => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const MOTIFS_CONNUS = [
  [/client\s*,?\s*absent/,                          'Client absent'],
  [/probleme d.?acces|pas d.?acces|acces impossible/, "Problème d'accès"],
  [/cheminement introuvable/,                       'Cheminement introuvable'],
  [/tampon scelle|chambre telecom inaccessible/,    'Chambre télécom inaccessible (tampon scellé)'],
  [/blocage gc/,                                    'Blocage GC (entre deux chambres)'],
  [/sur devis|intervention devis/,                  'Intervention sur DEVIS'],
  [/non eligible/,                                  'Client non éligible FTTH'],
  [/commande annul/,                                'Commande annulé par le client'],
  [/besoin arc|chambre sur voirie/,                 'Chambre sur voirie besoin ARC'],
  [/continuite (sur le )?pb/,                       'Pas de continuité PB PM'],
  [/pas de signal/,                                 'Pas de signal PM'],
  [/pbo .*satur|satur.* pbo/,                       'PBO Saturé'],
  [/erreur d.?adresse|probleme d.?adresse|mauvaise adresse/, "Erreur d'adresse"],
  [/travaux.*client|client.*travaux|client doit (faire|creer|poser)/, 'Travaux à réaliser par le client'],
  [/rdv report|rendez.?vous report|reprogramm|repousse|report.*rdv/, 'Report RDV suite demande client ou GEM'],
  [/binome|blesse/,                                 'Indisponibilité technicien ou binôme'],
  [/hydrocurage|hydraucurage|fourreau.*bouche/,     'Fourreau bouché / hydrocurage nécessaire'],
  [/autorisation|syndic|proprietaire/,              'Autorisation propriétaire ou syndic'],
];
function motifDejaListe(texte) {
  const t = sansAccents(texte);
  if (t.trim().length < 4) return null;
  const trouve = MOTIFS_CONNUS.find(([re]) => re.test(t));
  return trouve ? trouve[1] : null;
}
const TYPES_SITE = [
  'LOCAL PROFESSIONNEL',
  'IMMEUBLE',
  'PAVILLON',
  'CENTRE COMMERCIAL',
  'GALERIE MARCHANDE',
  'ERP (Établissement Recevant du Public)',
  'ENTREPÔT / ZONE INDUSTRIELLE',
  'ÉTABLISSEMENT SCOLAIRE',
  'ÉTABLISSEMENT DE SANTÉ',
];

function compressImage(dataUrl, maxPx = 1200, quality = 0.75) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxPx || h > maxPx) {
        if (w > h) { h = Math.round(h * maxPx / w); w = maxPx; }
        else { w = Math.round(w * maxPx / h); h = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = dataUrl;
  });
}

const PhotoSection = React.memo(function PhotoSection({ label, hint, photos, onChange, max = 10 }) {
  const [compressing, setCompressing] = useState(false);
  const handleAdd = async e => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    setCompressing(true);
    try {
      for (const file of files) {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async ev => {
            try {
              const compressed = await compressImage(ev.target.result);
              onChange(prev => [...prev, compressed]);
            } catch {}
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
    } finally {
      setCompressing(false);
    }
  };
  const remove = i => onChange(photos.filter((_, idx) => idx !== i));
  return (
    <div className="field">
      <div className="field-label">{label} ({photos.length}){compressing && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--attention-texte)' }}>⏳ Compression…</span>}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--texte-doux)', marginBottom: 6 }}>{hint}</div>}
      <div className="photo-grid">
        {photos.map((p, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={p} alt="" className="photo-thumb" />
            <div onClick={() => remove(i)} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, cursor: 'pointer' }}>✕</div>
          </div>
        ))}
        {(max === 0 || photos.length < max) && !compressing && (
          <>
            <label className="photo-add" style={{ flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 22 }}>📷</span>
              <span style={{ fontSize: 10, color: 'var(--texte-doux)' }}>Photo</span>
              <input type="file" accept="image/*" capture="environment" onChange={handleAdd} style={{ display: 'none' }} />
            </label>
            <label className="photo-add" style={{ flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 22 }}>🖼️</span>
              <span style={{ fontSize: 10, color: 'var(--texte-doux)' }}>Galerie</span>
              <input type="file" accept="image/*" onChange={handleAdd} style={{ display: 'none' }} multiple />
            </label>
          </>
        )}
      </div>
    </div>
  );
});

function GPSButton({ label, value, onChange }) {
  const [loading, setLoading] = useState(false);
  const getGPS = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => { onChange(`${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`); setLoading(false); },
      () => { alert('GPS non disponible'); setLoading(false); }
    );
  };
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder="Latitude, Longitude" style={{ flex: 1, padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
        <button onClick={getGPS} style={{ padding: '10px 14px', background: 'var(--accent-plein)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {loading ? '...' : '📍 GPS'}
        </button>
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid var(--bord)' }}>
      <span style={{ fontSize: 14, color: 'var(--texte)' }}>{label}</span>
      <div onClick={() => onChange(!value)} style={{ width: 44, height: 26, borderRadius: 13, background: value ? 'var(--accent)' : 'var(--bord-fort)', position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder = '' }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)', minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} />
    </div>
  );
}

function SectionTitle({ children, color = '#185FA5' }) {
  return <div style={{ background: 'var(--surface-3)', borderLeft: `4px solid ${color}`, borderRadius: '0 8px 8px 0', padding: '8px 12px', fontSize: 13, fontWeight: 700, color: 'var(--texte)', marginBottom: 12, marginTop: 16 }}>{children}</div>;
}

function InfoCard({ children }) {
  return <div style={{ background: 'var(--surface-3)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>{children}</div>;
}

export default function CompteRendu() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sigRef = useRef(null);
  const sigPercRef = useRef(null);

  const [dossier, setDossier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [offlineRestored, setOfflineRestored] = useState(false);

  // Résultat
  const [resultat, setResultat] = useState('CLEARED');
  const [typeEchec, setTypeEchec] = useState('');
  const [typeEchecAutre, setTypeEchecAutre] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [reportHeure, setReportHeure] = useState('');
  const [reportDemandeur, setReportDemandeur] = useState('');

  // Raccordement CLEARED
  const [typeRacc, setTypeRacc] = useState('RAC_I');
  const [usagePTO, setUsagePTO] = useState('Installation d\'une nouvelle prise optique');
  const [usagePTOManuallySet, setUsagePTOManuallySet] = useState(false);
  const [lgPublic, setLgPublic] = useState('');
  const [lgPrive, setLgPrive] = useState('');
  const [lgFauxPlafond, setLgFauxPlafond] = useState('');
  const [fpv, setFpv] = useState(false);
  const [percement, setPercement] = useState(false);
  const [nbPercementsComplexes, setNbPercementsComplexes] = useState('');
  const [hydrocurage, setHydrocurage] = useState(false);
  const [cpeInstalle, setCpeInstalle] = useState(false);

  // PTO
  const [refPTOModifiee, setRefPTOModifiee] = useState('');

  // ROP PBO
  const [ropRespectee, setRopRespectee] = useState(true);
  const [nvRefPBO, setNvRefPBO] = useState('');
  const [nvTubePBO, setNvTubePBO] = useState('');
  const [nvFibrePBO, setNvFibrePBO] = useState('');
  const [dechargeOi, setDechargeOi] = useState('');

  // PM
  const [positionRespecteepm, setPositionRespecteepm] = useState(true);
  const [nvPositionOperateurPM, setNvPositionOperateurPM] = useState('');
  const [nvPositionClientPM, setNvPositionClientPM] = useState('');
  const [dechargeOiPM, setDechargeOiPM] = useState('');

  // ONT
  const [constructeurOnt, setConstructeurOnt] = useState('');
  const [numSerieOnt, setNumSerieOnt] = useState('');
  const [showScanner, setShowScanner] = useState(false);

  // Clôture
  const [commentaireIntervention, setCommentaireIntervention] = useState('');
  const [dateArrivee, setDateArrivee] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [nomContact, setNomContact] = useState('');
  const [emailContact, setEmailContact] = useState('');
  const [signatureSite, setSignatureSite] = useState('');
  const [signedSite, setSignedSite] = useState(false);
  const [signaturePercement, setSignaturePercement] = useState('');
  const [signedPercement, setSignedPercement] = useState(false);
  const [nomSignatairePercement, setNomSignatairePercement] = useState('');

  // FAILED commun
  const [lgPublicFailed, setLgPublicFailed] = useState('');
  const [lgPriveFailed, setLgPriveFailed] = useState('');
  const [devis, setDevis] = useState(false);
  const [descBlockage, setDescBlockage] = useState('');
  const [noDecharge, setNoDecharge] = useState('');
  const [noDechargeOI, setNoDechargeOI] = useState('');

  // FAILED Chambre télécom inaccessible
  const [photoChambreInaccessibleEnv, setPhotoChambreInaccessibleEnv] = useState([]);
  const [photosChambreInaccessible, setPhotosChambreInaccessible] = useState([]);
  const [gpsChambreInaccessible, setGpsChambreInaccessible] = useState('');
  const [adresseChambreInaccessible, setAdresseChambreInaccessible] = useState('');

  // FAILED Blocage GC
  const [gpsChambreA, setGpsChambreA] = useState('');
  const [gpsChambreB, setGpsChambreB] = useState('');
  const [gpsPointBloquant, setGpsPointBloquant] = useState('');
  const [emplacementPointBloquant, setEmplacementPointBloquant] = useState('Trottoir');
  const [distABloquant, setDistABloquant] = useState('');
  const [distBBloquant, setDistBBloquant] = useState('');
  const [nbConduites, setNbConduites] = useState('');
  const [nbConduitesCassees, setNbConduitesCassees] = useState('');
  const [nbConduitesLsaturees, setNbConduitesLsaturees] = useState('');
  const [commentaireBlockage, setCommentaireBlockage] = useState('');

  // FAILED Devis complexe
  const [typeSite, setTypeSite] = useState('LOCAL PROFESSIONNEL');
  const [erp, setErp] = useState(false);
  const [erpCC, setErpCC] = useState(false);
  const [lgFauxPlafondDevis, setLgFauxPlafondDevis] = useState('');
  const [nacelle, setNacelle] = useState(false);
  const [goulotte, setGoulotte] = useState(false);
  const [lgGoulotte, setLgGoulotte] = useState('');
  const [hydrocurageDevis, setHydrocurageDevis] = useState(false);
  const [travGrandeHauteur, setTravGrandeHauteur] = useState(false);
  const [dureeEstimee, setDureeEstimee] = useState('');
  const [binome, setBinome] = useState(false);
  const [hno, setHno] = useState(false);
  const [emplacementPBODetail, setEmplacementPBODetail] = useState('');

  // Photos CLEARED
  const [photosFacade, setPhotosFacade] = useState([]);
  const [photosCheminement, setPhotosCheminement] = useState([]);
  const [photosPercementsComplexes, setPhotosPercementsComplexes] = useState([]);
  const [photoRefPTO, setPhotoRefPTO] = useState([]);
  const [photoPhotometrePTO, setPhotoPhotometrePTO] = useState([]);
  const [photoInstallationPTO, setPhotoInstallationPTO] = useState([]);
  const [photoPBOAvant, setPhotoPBOAvant] = useState([]);
  const [photoPBOApres, setPhotoPBOApres] = useState([]);
  const [photoRefFiCablePBO, setPhotoRefFiCablePBO] = useState([]);
  const [photoPMAvant, setPhotoPMAvant] = useState([]);
  const [photoPMApres, setPhotoPMApres] = useState([]);
  const [photoRefFiJarrPM, setPhotoRefFiJarrPM] = useState([]);
  const [photoPhotometrePM, setPhotoPhotometrePM] = useState([]);
  const [photoEtiquetteONT, setPhotoEtiquetteONT] = useState([]);
  const [photoVoyantONT, setPhotoVoyantONT] = useState([]);
  const [photoTestPPP, setPhotoTestPPP] = useState([]);
  const [photoTestActivation, setPhotoTestActivation] = useState([]);

  // VIA Test
  const [viaTestLoading, setViaTestLoading] = useState(false);
  const [viaTestError, setViaTestError]     = useState('');

  // FAILED ARC
  const [besoinARC, setBesoinARC] = useState(false);
  const [photosARC, setPhotosARC] = useState([]);
  const [gpsARC, setGpsARC] = useState('');
  const [adresseARC, setAdresseARC] = useState('');

  // Photos FAILED
  const [photoChambreAEnv, setPhotoChambreAEnv] = useState([]);
  const [photoChambreAOuverte, setPhotoChambreAOuverte] = useState([]);
  const [photoChambeBEnv, setPhotoChambeBEnv] = useState([]);
  const [photoChambreBOuverte, setPhotoChambreBOuverte] = useState([]);
  const [photoPointBloquant, setPhotoPointBloquant] = useState([]);
  const [photosCheminementFailed, setPhotosCheminementFailed] = useState([]);
  const [photoPBODevis, setPhotoPBODevis] = useState([]);
  const [photosEchec, setPhotosEchec] = useState([]);
  const [raisonAnnulationClient, setRaisonAnnulationClient] = useState('');
  const [photosAnnulationClient, setPhotosAnnulationClient] = useState([]);

  // FAILED Pas de continuité PB PM
  const [photoContinuitePBO, setPhotoContinuitePBO] = useState([]);
  const [photoSignalPM, setPhotoSignalPM] = useState([]);

  // FAILED Pas de signal PM
  const [photoSignalCoupleurPM, setPhotoSignalCoupleurPM] = useState([]);

  // FAILED Erreur d'adresse
  const [photosAdresseClient, setPhotosAdresseClient] = useState([]);
  const [gpsAdresseClient, setGpsAdresseClient] = useState('');

  // FAILED Chambre sur voirie besoin ARC
  const [photoChambreVoirieEnv, setPhotoChambreVoirieEnv] = useState([]);
  const [photosChambreVoirieDessus, setPhotosChambreVoirieDessus] = useState([]);
  const [gpsChambreVoirie, setGpsChambreVoirie] = useState('');
  const [adresseChambreVoirie, setAdresseChambreVoirie] = useState('');
  const [commentaireChambreVoirie, setCommentaireChambreVoirie] = useState('');

  // FAILED PBO Saturé
  const [photoPBOSature, setPhotoPBOSature] = useState([]);
  const [autrePBODisponible, setAutrePBODisponible] = useState(false);
  const [refPBODisponible, setRefPBODisponible] = useState('');
  const [photosPBODisponible, setPhotosPBODisponible] = useState([]);
  const [adressePBODisponible, setAdressePBODisponible] = useState('');
  const [gpsPBODisponible, setGpsPBODisponible] = useState('');
  const [arriveeMemePM, setArriveeMemePM] = useState(false);
  const [refNouvellePM, setRefNouvellePM] = useState('');

  const lgTotal = (parseFloat(lgPublic) || 0) + (parseFloat(lgPrive) || 0);
  const lgTotalFailed = (parseFloat(lgPublicFailed) || 0) + (parseFloat(lgPriveFailed) || 0);
  const isCleared = resultat === 'CLEARED';
  const isFailed = resultat === 'FAILED';
  const isBlockageGC = typeEchec === 'Blocage GC (entre deux chambres)';
  const isDevisComplex = typeEchec === 'Intervention sur DEVIS';
  const isCommandeAnnulee = typeEchec === 'Commande annulé par le client';
  const isChambreVoirieARC = typeEchec === 'Chambre sur voirie besoin ARC';
  const isPhotosBlocage = ['Client absent', 'RDVNH (rendez-vous non honoré faute technicien)', 'Cheminement introuvable'].includes(typeEchec);
  const isARC = ['Blocage GC (entre deux chambres)', 'Intervention sur DEVIS', 'Chambre télécom inaccessible (tampon scellé)', 'Autre'].includes(typeEchec);
  const isContinuitePBPM = typeEchec === 'Pas de continuité PB PM';
  const isSignalPM = typeEchec === 'Pas de signal PM';
  const isPBOSature = typeEchec === 'PBO Saturé';
  const isErreurAdresse = typeEchec === 'Erreur d\'adresse';

  const LOCAL_KEY = `cr_draft_${id}`;

  const saveLocalDraft = (payload, doss) => {
    saveDraft(LOCAL_KEY, payload, doss).catch(() => {});
  };

  const applyDraftData = (c) => {
    if (c.resultat) setResultat(c.resultat);
    if (c.type_echec) setTypeEchec(c.type_echec);
    if (c.type_echec_autre) setTypeEchecAutre(c.type_echec_autre);
    if (c.report_rdv) {
      setReportDate(c.report_rdv.date || '');
      setReportHeure(c.report_rdv.heure || '');
      setReportDemandeur(c.report_rdv.demandeur || '');
    }
    if (c.type_raccordement) setTypeRacc(c.type_raccordement);
    if (c.usage_pto) { setUsagePTO(c.usage_pto); setUsagePTOManuallySet(true); }
    if (c.ref_pto_modifiee) setRefPTOModifiee(c.ref_pto_modifiee);
    if (c.lg_public) { setLgPublic(c.lg_public); setLgPublicFailed(c.lg_public); }
    if (c.lg_prive) { setLgPrive(c.lg_prive); setLgPriveFailed(c.lg_prive); }
    if (c.lg_faux_plafond) { setLgFauxPlafond(c.lg_faux_plafond); setLgFauxPlafondDevis(c.lg_faux_plafond); }
    if (c.fpv !== undefined) setFpv(c.fpv);
    if (c.percement !== undefined) setPercement(c.percement);
    if (c.nb_percemnets_complexes) setNbPercementsComplexes(c.nb_percemnets_complexes);
    if (c.hydrocurage !== undefined) setHydrocurage(c.hydrocurage);
    if (c.cpe_installe !== undefined) setCpeInstalle(c.cpe_installe);
    if (c.nacelle !== undefined) setNacelle(c.nacelle);
    if (c.goulotte !== undefined) setGoulotte(c.goulotte);
    if (c.lg_goulotte) setLgGoulotte(c.lg_goulotte);
    if (c.binome !== undefined) setBinome(c.binome);
    if (c.hno !== undefined) setHno(c.hno);
    if (c.duree_estimee) setDureeEstimee(c.duree_estimee);
    if (c.trav_grande_hauteur !== undefined) setTravGrandeHauteur(c.trav_grande_hauteur);
    if (c.rop_respectee !== undefined) setRopRespectee(c.rop_respectee);
    if (c.nv_ref_pbo) setNvRefPBO(c.nv_ref_pbo);
    if (c.nv_tube_pbo) setNvTubePBO(c.nv_tube_pbo);
    if (c.nv_fibre_pbo) setNvFibrePBO(c.nv_fibre_pbo);
    if (c.decharge_oi) setDechargeOi(c.decharge_oi);
    if (c.position_respectee_pm !== undefined) setPositionRespecteepm(c.position_respectee_pm);
    if (c.nv_position_operateur_pm) setNvPositionOperateurPM(c.nv_position_operateur_pm);
    if (c.nv_position_client_pm) setNvPositionClientPM(c.nv_position_client_pm);
    if (c.decharge_oi_pm) setDechargeOiPM(c.decharge_oi_pm);
    if (c.constructeur_ont) setConstructeurOnt(c.constructeur_ont);
    if (c.numero_serie_ont) setNumSerieOnt(c.numero_serie_ont);
    if (c.commentaire_intervention) setCommentaireIntervention(c.commentaire_intervention);
    if (c.date_arrivee) setDateArrivee(c.date_arrivee);
    if (c.date_fin) setDateFin(c.date_fin);
    if (c.nom_contact) setNomContact(c.nom_contact);
    if (c.email_contact) setEmailContact(c.email_contact);
    if (c.no_decharge) setNoDecharge(c.no_decharge);
    if (c.no_decharge_oi) setNoDechargeOI(c.no_decharge_oi);
    if (c.signature_site) { setSignatureSite(c.signature_site); setSignedSite(true); }
    if (c.signature_percement) { setSignaturePercement(c.signature_percement); setSignedPercement(true); }
    if (c.nom_signataire_percement) setNomSignatairePercement(c.nom_signataire_percement);
    if (c.desc_blockage) setDescBlockage(c.desc_blockage);
    if (c.commentaire_blockage) setCommentaireBlockage(c.commentaire_blockage);
    if (c.nb_conduites) setNbConduites(c.nb_conduites);
    if (c.nb_conduites_cassees) setNbConduitesCassees(c.nb_conduites_cassees);
    if (c.nb_conduites_saturees) setNbConduitesLsaturees(c.nb_conduites_saturees);
    if (c.gps_chambre_inaccessible) setGpsChambreInaccessible(c.gps_chambre_inaccessible);
    if (c.adresse_chambre_inaccessible) setAdresseChambreInaccessible(c.adresse_chambre_inaccessible);
    if (c.gps_chambre_a) setGpsChambreA(c.gps_chambre_a);
    if (c.gps_chambre_b) setGpsChambreB(c.gps_chambre_b);
    if (c.gps_point_bloquant) setGpsPointBloquant(c.gps_point_bloquant);
    if (c.emplacement_point_bloquant) setEmplacementPointBloquant(c.emplacement_point_bloquant);
    if (c.dist_a_bloquant) setDistABloquant(c.dist_a_bloquant);
    if (c.dist_b_bloquant) setDistBBloquant(c.dist_b_bloquant);
    if (c.type_site) setTypeSite(c.type_site);
    if (c.erp !== undefined) setErp(c.erp);
    if (c.erp_cc !== undefined) setErpCC(c.erp_cc);
    if (c.emplacement_pbo_detail) setEmplacementPBODetail(c.emplacement_pbo_detail);
    if (c.photos_facade?.length) setPhotosFacade(c.photos_facade);
    if (c.photos_cheminement?.length) setPhotosCheminement(c.photos_cheminement);
    if (c.photos_percemnets_complexes?.length) setPhotosPercementsComplexes(c.photos_percemnets_complexes);
    if (c.photo_ref_pto?.length) setPhotoRefPTO(c.photo_ref_pto);
    if (c.photo_photometre_pto?.length) setPhotoPhotometrePTO(c.photo_photometre_pto);
    if (c.photo_installation_pto?.length) setPhotoInstallationPTO(c.photo_installation_pto);
    if (c.photo_pbo_avant?.length) setPhotoPBOAvant(c.photo_pbo_avant);
    if (c.photo_pbo_apres?.length) setPhotoPBOApres(c.photo_pbo_apres);
    if (c.photo_ref_fi_cable_pbo?.length) setPhotoRefFiCablePBO(c.photo_ref_fi_cable_pbo);
    if (c.photo_pm_avant?.length) setPhotoPMAvant(c.photo_pm_avant);
    if (c.photo_pm_apres?.length) setPhotoPMApres(c.photo_pm_apres);
    if (c.photo_ref_fi_jarr_pm?.length) setPhotoRefFiJarrPM(c.photo_ref_fi_jarr_pm);
    if (c.photo_photometre_pm?.length) setPhotoPhotometrePM(c.photo_photometre_pm);
    if (c.photo_etiquette_ont?.length) setPhotoEtiquetteONT(c.photo_etiquette_ont);
    if (c.photo_voyant_ont?.length) setPhotoVoyantONT(c.photo_voyant_ont);
    if (c.photo_test_ppp?.length) setPhotoTestPPP(c.photo_test_ppp);
    if (c.photo_test_activation?.length) setPhotoTestActivation(c.photo_test_activation);
    if (c.photo_chambre_inaccessible_env?.length) setPhotoChambreInaccessibleEnv(c.photo_chambre_inaccessible_env);
    if (c.photos_chambre_inaccessible?.length) setPhotosChambreInaccessible(c.photos_chambre_inaccessible);
    if (c.photo_chambre_a_env?.length) setPhotoChambreAEnv(c.photo_chambre_a_env);
    if (c.photo_chambre_a_ouverte?.length) setPhotoChambreAOuverte(c.photo_chambre_a_ouverte);
    if (c.photo_chambre_b_env?.length) setPhotoChambeBEnv(c.photo_chambre_b_env);
    if (c.photo_chambre_b_ouverte?.length) setPhotoChambreBOuverte(c.photo_chambre_b_ouverte);
    if (c.photo_point_bloquant?.length) setPhotoPointBloquant(c.photo_point_bloquant);
    if (c.photos_cheminement_failed?.length) setPhotosCheminementFailed(c.photos_cheminement_failed);
    if (c.photo_pbo_devis?.length) setPhotoPBODevis(c.photo_pbo_devis);
    if (c.photos_echec?.length) setPhotosEchec(c.photos_echec);
    if (c.besoin_arc !== undefined) setBesoinARC(c.besoin_arc);
    if (c.photos_arc?.length) setPhotosARC(c.photos_arc);
    if (c.gps_arc) setGpsARC(c.gps_arc);
    if (c.adresse_arc) setAdresseARC(c.adresse_arc);
    if (c.raison_annulation_client) setRaisonAnnulationClient(c.raison_annulation_client);
    if (c.photos_annulation_client?.length) setPhotosAnnulationClient(c.photos_annulation_client);
    if (c.photo_chambre_voirie_env?.length) setPhotoChambreVoirieEnv(c.photo_chambre_voirie_env);
    if (c.photos_chambre_voirie_dessus?.length) setPhotosChambreVoirieDessus(c.photos_chambre_voirie_dessus);
    if (c.gps_chambre_voirie) setGpsChambreVoirie(c.gps_chambre_voirie);
    if (c.adresse_chambre_voirie) setAdresseChambreVoirie(c.adresse_chambre_voirie);
    if (c.commentaire_chambre_voirie) setCommentaireChambreVoirie(c.commentaire_chambre_voirie);
    if (c.photo_continuite_pbo?.length) setPhotoContinuitePBO(c.photo_continuite_pbo);
    if (c.photo_signal_pm?.length) setPhotoSignalPM(c.photo_signal_pm);
    if (c.photo_signal_coupleur_pm?.length) setPhotoSignalCoupleurPM(c.photo_signal_coupleur_pm);
    if (c.photos_adresse_client?.length) setPhotosAdresseClient(c.photos_adresse_client);
    if (c.gps_adresse_client) setGpsAdresseClient(c.gps_adresse_client);
    if (c.photo_pbo_sature?.length) setPhotoPBOSature(c.photo_pbo_sature);
    if (c.autre_pbo_disponible !== undefined) setAutrePBODisponible(c.autre_pbo_disponible);
    if (c.ref_pbo_disponible) setRefPBODisponible(c.ref_pbo_disponible);
    if (c.photos_pbo_disponible?.length) setPhotosPBODisponible(c.photos_pbo_disponible);
    if (c.adresse_pbo_disponible) setAdressePBODisponible(c.adresse_pbo_disponible);
    if (c.gps_pbo_disponible) setGpsPBODisponible(c.gps_pbo_disponible);
    if (c.arrivee_meme_pm !== undefined) setArriveeMemePM(c.arrivee_meme_pm);
    if (c.ref_nouvelle_pm) setRefNouvellePM(c.ref_nouvelle_pm);
    if (c.pdf_url) setPdfUrl(c.pdf_url);
  };

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      const localDraft = await loadDraft(LOCAL_KEY).catch(() => null);
      try {
        const [dData, crData] = await Promise.all([getDossier(id), getCR(id)]);
        setDossier(dData.dossier);
        if (dData.dossier) {
          setRefPTOModifiee(dData.dossier.ref_pto || '');
          if (dData.dossier.email_contact_site) setEmailContact(dData.dossier.email_contact_site);
        }
        if (crData.cr) {
          const c = crData.cr;
          const serverTs = c._ts || 0;
          const localTs = localDraft?._ts || 0;
          if (localTs > serverTs + 60000 && localDraft?.cr) {
            applyDraftData(localDraft.cr);
            if (localDraft.dossier) setDossier(prev => prev || localDraft.dossier);
            setOfflineRestored(true);
          } else {
            applyDraftData(c);
          }
          saveLocalDraft(c, dData.dossier);
        } else if (localDraft?.cr) {
          applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        }
      } catch {
        // Serveur injoignable — restaurer depuis IndexedDB
        if (localDraft) {
          if (localDraft.dossier) setDossier(localDraft.dossier);
          if (localDraft.cr) applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        }
      }
    };
    load();
  }, [id]);

  // Auto-select usagePTO selon typeRacc (sauf si déjà saisi manuellement depuis un CR sauvegardé)
  useEffect(() => {
    if (usagePTOManuallySet) return;
    setUsagePTO(typeRacc === 'PTO_C' ? "Utilisation d'une prise existante" : "Installation d'une nouvelle prise optique");
  }, [typeRacc]);

  // Détection connexion internet
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Ref pour l'auto-save visibilitychange — mis à jour après chaque render
  const autoSaveRef = useRef(null);

  const captureSignatureSite = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) { setSignatureSite(sigRef.current.toDataURL('image/png')); setSignedSite(true); }
  };
  const captureSignaturePercement = () => {
    if (sigPercRef.current && !sigPercRef.current.isEmpty()) { setSignaturePercement(sigPercRef.current.toDataURL('image/png')); setSignedPercement(true); }
  };

  const buildPayload = (final) => ({
    dossier_id: id, final, type_cr: 'CRI', resultat, type_echec: typeEchec, type_echec_autre: typeEchecAutre,
    report_rdv: (resultat === 'FAILED' && typeEchec === MOTIF_REPORT)
      ? { date: reportDate, heure: reportHeure, demandeur: reportDemandeur }
      : null,
    type_raccordement: typeRacc, usage_pto: usagePTO,
    ref_pto_modifiee: refPTOModifiee,
    lg_public: isCleared ? lgPublic : lgPublicFailed,
    lg_prive: isCleared ? lgPrive : lgPriveFailed,
    lg_total: isCleared ? lgTotal : lgTotalFailed,
    lg_faux_plafond: isCleared ? lgFauxPlafond : lgFauxPlafondDevis,
    fpv: isCleared ? fpv : false,
    percement, nb_percemnets_complexes: nbPercementsComplexes,
    hydrocurage: isCleared ? hydrocurage : hydrocurageDevis,
    nacelle, goulotte, lg_goulotte: lgGoulotte, binome, hno,
    duree_estimee: dureeEstimee, devis,
    rop_respectee: ropRespectee,
    nv_ref_pbo: nvRefPBO, nv_tube_pbo: nvTubePBO, nv_fibre_pbo: nvFibrePBO,
    decharge_oi: dechargeOi,
    position_respectee_pm: positionRespecteepm,
    nv_position_operateur_pm: nvPositionOperateurPM,
    nv_position_client_pm: nvPositionClientPM,
    decharge_oi_pm: dechargeOiPM,
    constructeur_ont: constructeurOnt, numero_serie_ont: numSerieOnt,
    cpe_installe: cpeInstalle,
    commentaire_intervention: commentaireIntervention,
    commentaire_blockage: commentaireBlockage,
    nb_conduites: nbConduites,
    nb_conduites_cassees: nbConduitesCassees,
    nb_conduites_saturees: nbConduitesLsaturees,
    emplacement_pbo_detail: emplacementPBODetail,
    desc_blockage: descBlockage,
    type_site: typeSite, erp, erp_cc: erpCC,
    trav_grande_hauteur: travGrandeHauteur,
    date_arrivee: dateArrivee, date_fin: dateFin,
    nom_contact: nomContact, email_contact: emailContact,
    no_decharge: noDecharge,
    no_decharge_oi: noDechargeOI,
    signature_site: signatureSite, signature_percement: signaturePercement,
    nom_signataire_percement: nomSignatairePercement,
    photo_chambre_inaccessible_env: photoChambreInaccessibleEnv,
    photos_chambre_inaccessible: photosChambreInaccessible,
    gps_chambre_inaccessible: gpsChambreInaccessible,
    adresse_chambre_inaccessible: adresseChambreInaccessible,
    gps_chambre_a: gpsChambreA, gps_chambre_b: gpsChambreB,
    gps_point_bloquant: gpsPointBloquant,
    emplacement_point_bloquant: emplacementPointBloquant,
    dist_a_bloquant: distABloquant, dist_b_bloquant: distBBloquant,
    photos_facade: photosFacade, photos_cheminement: photosCheminement,
    photos_percemnets_complexes: photosPercementsComplexes,
    photo_ref_pto: photoRefPTO, photo_photometre_pto: photoPhotometrePTO,
    photo_pbo_avant: photoPBOAvant, photo_pbo_apres: photoPBOApres,
    photo_ref_fi_cable_pbo: photoRefFiCablePBO,
    photo_pm_avant: photoPMAvant, photo_pm_apres: photoPMApres,
    photo_ref_fi_jarr_pm: photoRefFiJarrPM,
    photo_photometre_pm: photoPhotometrePM,
    photo_etiquette_ont: photoEtiquetteONT, photo_voyant_ont: photoVoyantONT,
    photo_test_ppp: photoTestPPP, photo_test_activation: photoTestActivation,
    photo_chambre_a_env: photoChambreAEnv, photo_chambre_a_ouverte: photoChambreAOuverte,
    photo_chambre_b_env: photoChambeBEnv, photo_chambre_b_ouverte: photoChambreBOuverte,
    photo_point_bloquant: photoPointBloquant,
    photos_cheminement_failed: photosCheminementFailed,
    photo_pbo_devis: photoPBODevis,
    photos_echec: photosEchec,
    besoin_arc: besoinARC,
    photos_arc: photosARC,
    gps_arc: gpsARC,
    adresse_arc: adresseARC,
    raison_annulation_client: raisonAnnulationClient,
    photos_annulation_client: photosAnnulationClient,
    photo_chambre_voirie_env: photoChambreVoirieEnv,
    photos_chambre_voirie_dessus: photosChambreVoirieDessus,
    gps_chambre_voirie: gpsChambreVoirie,
    adresse_chambre_voirie: adresseChambreVoirie,
    commentaire_chambre_voirie: commentaireChambreVoirie,
    photo_continuite_pbo: photoContinuitePBO,
    photo_signal_pm: photoSignalPM,
    photo_signal_coupleur_pm: photoSignalCoupleurPM,
    photos_adresse_client: photosAdresseClient,
    gps_adresse_client: gpsAdresseClient,
    photo_pbo_sature: photoPBOSature,
    autre_pbo_disponible: autrePBODisponible,
    ref_pbo_disponible: refPBODisponible,
    photos_pbo_disponible: photosPBODisponible,
    adresse_pbo_disponible: adressePBODisponible,
    gps_pbo_disponible: gpsPBODisponible,
    arrivee_meme_pm: arriveeMemePM,
    ref_nouvelle_pm: refNouvellePM,
  });

  // Mettre à jour la ref après chaque render pour que le handler visibilitychange ait le payload à jour
  autoSaveRef.current = { dossier, generating, pdfUrl, buildPayload, saveLocalDraft, LOCAL_KEY };
  useEffect(() => {
    const handleHide = () => {
      if (!document.hidden) return;
      const s = autoSaveRef.current;
      if (s && s.dossier && !s.generating && !s.pdfUrl) {
        const payload = s.buildPayload(false);
        // Sauvegarde synchrone texte en priorité (iOS suspend le JS très vite)
        saveDraftSync(s.LOCAL_KEY, payload, s.dossier);
        // Sauvegarde async IDB (photos incluses) — peut ne pas finir sur iOS
        s.saveLocalDraft(payload, s.dossier);
        try { saveCR(payload); } catch {}
      }
    };
    document.addEventListener('visibilitychange', handleHide);
    return () => document.removeEventListener('visibilitychange', handleHide);
  }, []);

  // Sauvegarde locale périodique toutes les 30s (backup offline)
  useEffect(() => {
    const timer = setInterval(() => {
      const s = autoSaveRef.current;
      if (s && s.dossier && !s.generating && !s.pdfUrl) {
        s.saveLocalDraft(s.buildPayload(false), s.dossier);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleSaveDraft = async () => {
    setSaving(true); setMessage('');
    const payload = buildPayload(false);
    // Sauvegarde locale AVANT tout (photos incluses, IndexedDB)
    const localOk = await saveDraft(LOCAL_KEY, payload, dossier).catch(() => false);
    const localMsg = localOk === true    ? '✓ Sauvegardé localement (photos incluses)'
                   : localOk === 'fallback' ? '⚠ Sauvegardé sans photos (iPhone — stockage limité). Revenez vite !'
                   : '⚠ Erreur sauvegarde locale — vérifiez l\'espace disponible';
    if (!isOnline) {
      setSaving(false);
      setMessage(localMsg);
      return;
    }
    try { await saveCR(payload); setMessage(localOk ? '✓ Brouillon sauvegardé (local + serveur)' : '✓ Brouillon sauvegardé sur le serveur'); }
    catch { setMessage(localOk ? '✓ Sauvegardé localement — erreur serveur' : 'Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleBack = async () => {
    // Auto-save seulement si le CR n'a pas encore été généré (pas de PDF)
    if (dossier && !generating && !pdfUrl) {
      try { await saveCR(buildPayload(false)); } catch {}
    }
    navigate(`/dossier/${id}`);
  };

  const handleGenerate = async () => {
    if (!isOnline) {
      setMessage('Impossible de générer le PDF hors ligne. Reconnectez-vous puis réessayez.');
      return;
    }
    if (resultat === 'FAILED' && !typeEchec) {
      setMessage('Le type d\'échec est obligatoire pour un résultat FAILED');
      return;
    }
    if (resultat === 'FAILED' && typeEchec === MOTIF_REPORT
        && (!reportDate || !reportHeure || !reportDemandeur)) {
      setMessage('Report de RDV : indiquez la nouvelle date, l’heure et qui l’a demandé');
      return;
    }
    if (resultat === 'FAILED' && typeEchec === 'Autre') {
      if (!typeEchecAutre.trim()) {
        setMessage('Précisez le motif dans le champ « Autre »');
        return;
      }
      const existant = motifDejaListe(typeEchecAutre);
      if (existant) {
        setMessage(`Ce motif existe dans la liste : sélectionnez « ${existant} » au lieu de « Autre »`);
        return;
      }
    }
    if (resultat === 'FAILED' && !noDecharge.trim()) {
      setMessage('Le N° de décharge COVAGE est obligatoire pour un résultat FAILED');
      return;
    }
    setGenerating(true); setMessage('');
    try {
      const data = await saveCR(buildPayload(true));
      setPdfUrl(data.pdf_url);
      setMessage('PDF généré avec succès !');
      removeDraft(LOCAL_KEY).catch(() => {}); // nettoyer le brouillon local
    } catch { setMessage('Erreur lors de la génération du PDF'); }
    finally { setGenerating(false); }
  };

  const handleViaTest = async () => {
    setViaTestLoading(true);
    setViaTestError('');
    try {
      // 1. Lancer le test — retourne immédiatement un jobId
      const { data: startData } = await axios.post('/api/test-via', { via_code: dossier.n_via }, { timeout: 10000 });
      const jobId = startData.jobId;
      if (!jobId) throw new Error(startData.error || 'Erreur démarrage test');

      // 2. Polling toutes les 5s jusqu'au résultat (max 3 min)
      const maxAttempts = 36;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const { data } = await axios.get(`/api/test-via/${jobId}`, { timeout: 10000 });
        if (data.status === 'done') {
          const compressed = await compressImage(`data:image/png;base64,${data.screenshot}`);
          setPhotoTestActivation([compressed]);
          return;
        }
        if (data.status === 'error') {
          setViaTestError(data.error || 'Erreur test VIA');
          if (data.debug_screenshot) {
            const compressed = await compressImage(`data:image/png;base64,${data.debug_screenshot}`);
            setPhotoTestActivation([compressed]);
          }
          return;
        }
        // status === 'pending' → on continue le polling
      }
      setViaTestError('Délai dépassé — le test n\'a pas répondu en 3 minutes');
    } catch (err) {
      setViaTestError(err.response?.data?.error || err.message || 'Erreur inconnue');
    } finally {
      setViaTestLoading(false);
    }
  };

  if (!dossier) return <div className="app-shell"><div className="topbar"><div className="topbar-title">Chargement…</div></div></div>;

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="back-btn" onClick={handleBack}>‹</div>
        <div className="topbar-title">Compte-rendu</div>
        <div style={{ fontSize: 11, color: 'var(--texte-doux)' }}>{dossier.ref_commande}</div>
      </div>

      {!isOnline && (
        <div style={{ background: 'var(--attention-fond)', color: 'var(--attention-texte)', padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center', borderBottom: '1px solid var(--attention-bord)' }}>
          Hors ligne — données sauvegardées localement
        </div>
      )}
      {offlineRestored && (
        <div style={{ background: 'var(--succes-fond)', color: 'var(--succes-texte)', padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center', borderBottom: '1px solid var(--succes-bord)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Données restaurées depuis la sauvegarde locale</span>
          <span style={{ cursor: 'pointer', fontWeight: 400 }} onClick={() => setOfflineRestored(false)}>✕</span>
        </div>
      )}

      <div className="content">

        {/* PHOTO FACADE */}
        <SectionTitle>Photo façade du site</SectionTitle>
        <PhotoSection label="Photo façade (obligatoire)" photos={photosFacade} onChange={setPhotosFacade} max={3} />

        {/* RESULTAT */}
        <SectionTitle>Résultat de l'intervention</SectionTitle>
        <div className="field">
          <div className="field-label">Résultat</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['CLEARED', 'FAILED'].map(r => (
              <div key={r} onClick={() => setResultat(r)} style={{ flex: 1, padding: '12px 8px', textAlign: 'center', borderRadius: 10, border: `2px solid ${resultat === r ? (r === 'CLEARED' ? 'var(--succes-bord)' : 'var(--danger)') : 'var(--bord-fort)'}`, background: resultat === r ? (r === 'CLEARED' ? 'var(--succes-fond)' : 'var(--danger-fond)') : 'var(--surface-2)', color: resultat === r ? (r === 'CLEARED' ? 'var(--succes-texte)' : 'var(--danger-texte)') : 'var(--texte-doux)', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>{r}</div>
            ))}
          </div>
        </div>

        {/* ========== FAILED ========== */}
        {isFailed && (
          <>
            <div className="field">
              <div className="field-label">Type d'échec</div>
              <select value={typeEchec} onChange={e => setTypeEchec(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: `1.5px solid ${!typeEchec ? 'var(--danger)' : 'var(--bord-fort)'}`, borderRadius: 10, fontSize: 14, background: !typeEchec ? 'var(--danger-fond)' : 'var(--surface-2)', color: 'var(--texte)' }}>
                <option value="">-- Sélectionner le type d'échec --</option>
                {TYPES_ECHEC.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {!typeEchec && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>Obligatoire pour générer le PDF</div>}
              {typeEchec === 'Autre' && (() => {
                const vide    = !typeEchecAutre.trim();
                const existant = motifDejaListe(typeEchecAutre);
                return (
                  <>
                    <textarea
                      value={typeEchecAutre}
                      onChange={e => setTypeEchecAutre(e.target.value)}
                      placeholder="Décrivez précisément le motif (obligatoire)…"
                      style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1.5px solid ${vide ? 'var(--danger)' : 'var(--bord-fort)'}`, borderRadius: 10, fontSize: 14, background: vide ? 'var(--danger-fond)' : 'var(--surface-2)', color: 'var(--texte)', minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                    {vide && (
                      <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                        Obligatoire : précisez le motif pour générer le PDF
                      </div>
                    )}
                    {existant && (
                      <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--attention-fond)', border: '1px solid var(--attention-bord)', fontSize: 13, color: 'var(--attention-texte)' }}>
                        Ce motif existe déjà dans la liste : <strong>{existant}</strong>.<br />
                        Merci de le sélectionner ci-dessus plutôt que d'utiliser « Autre ».
                      </div>
                    )}
                  </>
                );
              })()}

              {typeEchec === MOTIF_REPORT && (
                <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 10, background: 'var(--info-fond)', border: '1px solid var(--info-bord)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--info-texte)', marginBottom: 8 }}>
                    Nouveau rendez-vous
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--info-texte)', marginBottom: 10 }}>
                    La planification appliquera cette date. Les trois champs sont obligatoires.
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <input type="date" value={reportDate} min={new Date().toISOString().slice(0, 10)}
                      onChange={e => setReportDate(e.target.value)}
                      style={{ flex: 2, minWidth: 0, padding: '10px 12px', fontSize: 14, borderRadius: 10, background: 'var(--surface-2)', color: 'var(--texte)',
                               border: `1.5px solid ${reportDate ? 'var(--bord-fort)' : 'var(--danger)'}` }} />
                    <input type="time" value={reportHeure}
                      onChange={e => setReportHeure(e.target.value)}
                      style={{ flex: 1, minWidth: 0, padding: '10px 12px', fontSize: 14, borderRadius: 10, background: 'var(--surface-2)', color: 'var(--texte)',
                               border: `1.5px solid ${reportHeure ? 'var(--bord-fort)' : 'var(--danger)'}` }} />
                  </div>

                  <div style={{ fontSize: 12, color: 'var(--info-texte)', marginBottom: 6 }}>Report demandé par</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {DEMANDEURS_REPORT.map(d => (
                      <div key={d} onClick={() => setReportDemandeur(d)} style={{
                        padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
                        border: `1.5px solid ${reportDemandeur === d ? 'var(--accent)' : reportDemandeur ? 'var(--bord-fort)' : 'var(--danger)'}`,
                        background: reportDemandeur === d ? 'var(--info-fond)' : 'var(--surface-2)',
                        color: reportDemandeur === d ? 'var(--accent)' : 'var(--texte-doux)',
                        fontWeight: reportDemandeur === d ? 700 : 400,
                      }}>{d}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="field">
              <div className="field-label">Type de raccordement à réaliser</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {TYPES_RACCORDEMENT.map(t => (
                  <div key={t} onClick={() => setTypeRacc(t)} style={{ padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${typeRacc === t ? 'var(--danger)' : 'var(--bord-fort)'}`, background: typeRacc === t ? 'var(--danger-fond)' : 'var(--surface-2)', color: typeRacc === t ? 'var(--danger-texte)' : 'var(--texte-doux)', fontWeight: typeRacc === t ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>{t}</div>
                ))}
              </div>
            </div>

            {!isCommandeAnnulee && !isPBOSature && <>
              <Input label="Longueur câble domaine public (ml)" value={lgPublicFailed} onChange={setLgPublicFailed} type="number" />
              <Input label="Longueur câble domaine privé (ml)" value={lgPriveFailed} onChange={setLgPriveFailed} type="number" />
              <InfoCard><strong>Longueur totale calculée : {lgTotalFailed} ml</strong></InfoCard>
            </>}
          </>
        )}

        {/* FAILED — Chambre télécom inaccessible */}
        {isFailed && typeEchec === 'Chambre télécom inaccessible (tampon scellé)' && (
          <>
            <SectionTitle color="#D42B2B">Chambre télécom inaccessible</SectionTitle>
            <PhotoSection label="Photo de la chambre dans son environnement" hint="Permettant de situer les travaux (chaussée, trottoir...)" photos={photoChambreInaccessibleEnv} onChange={setPhotoChambreInaccessibleEnv} max={3} />
            <PhotoSection label="Photos de la chambre inaccessible (vue du dessus)" photos={photosChambreInaccessible} onChange={setPhotosChambreInaccessible} max={0} />
            <GPSButton label="Emplacement GPS de la chambre" value={gpsChambreInaccessible} onChange={setGpsChambreInaccessible} />
            <Input label="Adresse de la chambre inaccessible" value={adresseChambreInaccessible} onChange={setAdresseChambreInaccessible} placeholder="Ex: devant le 12 rue de la Paix, 75001 Paris" />
          </>
        )}

        {/* FAILED — Blocage GC */}
        {isFailed && isBlockageGC && (
          <>
            <SectionTitle color="#D42B2B">Blocage GC — Chambre A</SectionTitle>
            <PhotoSection label="Photo chambre A dans son environnement" photos={photoChambreAEnv} onChange={setPhotoChambreAEnv} max={3} />
            <PhotoSection label="Photo chambre A ouverte avec aiguille dans l'alvéole" photos={photoChambreAOuverte} onChange={setPhotoChambreAOuverte} max={3} />
            <GPSButton label="Position GPS Chambre A" value={gpsChambreA} onChange={setGpsChambreA} />

            <SectionTitle color="#D42B2B">Blocage GC — Chambre B</SectionTitle>
            <PhotoSection label="Photo chambre B dans son environnement" photos={photoChambeBEnv} onChange={setPhotoChambeBEnv} max={3} />
            <PhotoSection label="Photo chambre B ouverte avec aiguille dans l'alvéole" photos={photoChambreBOuverte} onChange={setPhotoChambreBOuverte} max={3} />
            <GPSButton label="Position GPS Chambre B" value={gpsChambreB} onChange={setGpsChambreB} />

            <SectionTitle color="#D42B2B">Point bloquant</SectionTitle>
            <PhotoSection label="Photo point bloquant avec plot" photos={photoPointBloquant} onChange={setPhotoPointBloquant} max={5} />
            <GPSButton label="Position GPS point bloquant" value={gpsPointBloquant} onChange={setGpsPointBloquant} />
            <div className="field">
              <div className="field-label">Emplacement point bloquant</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['Trottoir', 'Chaussée', 'Pleine terre'].map(opt => (
                  <div key={opt} onClick={() => setEmplacementPointBloquant(opt)} style={{ flex: 1, padding: '8px', textAlign: 'center', borderRadius: 10, border: `1.5px solid ${emplacementPointBloquant === opt ? 'var(--danger)' : 'var(--bord-fort)'}`, background: emplacementPointBloquant === opt ? 'var(--danger-fond)' : 'var(--surface-2)', color: emplacementPointBloquant === opt ? 'var(--danger-texte)' : 'var(--texte-doux)', fontWeight: emplacementPointBloquant === opt ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>{opt}</div>
                ))}
              </div>
            </div>
            <Input label="Distance Chambre A → Point bloquant (ml)" value={distABloquant} onChange={setDistABloquant} type="number" />
            <Input label="Distance Chambre B → Point bloquant (ml)" value={distBBloquant} onChange={setDistBBloquant} type="number" />
            <Input label="Nombre de conduites entre les 2 extrémités" value={nbConduites} onChange={setNbConduites} type="number" placeholder="Ex: 4" />
            <Input label="Nombre de conduites cassées (avec aiguillage testé mais impossible)" value={nbConduitesCassees} onChange={setNbConduitesCassees} type="number" placeholder="Ex: 2" />
            <Input label="Nombre de conduites saturées (avec aiguillage testé mais impossible)" value={nbConduitesLsaturees} onChange={setNbConduitesLsaturees} type="number" placeholder="Ex: 1" />
            <Textarea label="Commentaire du blocage" value={commentaireBlockage} onChange={setCommentaireBlockage} placeholder="Décrire le blocage en détail..." />
          </>
        )}

        {/* FAILED — Devis complexe */}
        {isFailed && isDevisComplex && (
          <>
            <SectionTitle color="#D42B2B">Intervention sur devis — Détails</SectionTitle>
            <div className="field">
              <div className="field-label">Type de site à raccorder</div>
              <select value={typeSite} onChange={e => setTypeSite(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }}>
                {TYPES_SITE.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Input label="Longueur faux plafond (ml)" value={lgFauxPlafondDevis} onChange={setLgFauxPlafondDevis} type="number" />
            <Input label="Nombre d'heures nécessaires à l'intervention" value={dureeEstimee} onChange={setDureeEstimee} type="number" placeholder="Ex: 3" />
            <Toggle label="Travaux grande hauteur / Nacelle intérieure" value={travGrandeHauteur} onChange={setTravGrandeHauteur} />
            <Toggle label="Pose de goulotte" value={goulotte} onChange={setGoulotte} />
            {goulotte && <Input label="Longueur goulotte (ml)" value={lgGoulotte} onChange={setLgGoulotte} type="number" />}
            <Toggle label="Hydrocurage / débouchage de fourreau" value={hydrocurageDevis} onChange={setHydrocurageDevis} />
            <Toggle label="Binôme nécessaire" value={binome} onChange={setBinome} />
            <Toggle label="Intervention de nuit (HNO)" value={hno} onChange={setHno} />
            <Textarea label="Description détaillée du blocage" value={descBlockage} onChange={setDescBlockage} placeholder="Décrire les contraintes en détail..." />
            <Textarea label="Emplacement PBO détaillé" value={emplacementPBODetail} onChange={setEmplacementPBODetail} />
            <PhotoSection label="Photos du cheminement à prévoir (sans limite)" photos={photosCheminementFailed} onChange={setPhotosCheminementFailed} max={0} />
            <PhotoSection label="Photo du PBO" photos={photoPBODevis} onChange={setPhotoPBODevis} max={3} />
          </>
        )}

        {/* FAILED — Client absent / RDVNH / Cheminement introuvable */}
        {isFailed && isPhotosBlocage && typeEchec && (
          <>
            <SectionTitle color="#D42B2B">Photos du blocage</SectionTitle>
            <PhotoSection label="Photos du blocage (sans limite)" photos={photosCheminementFailed} onChange={setPhotosCheminementFailed} max={0} />
            <Textarea label="Description de l'échec" value={descBlockage} onChange={setDescBlockage} placeholder="Expliquer la raison de l'échec..." />
          </>
        )}

        {/* FAILED — Problème d'accès */}
        {isFailed && typeEchec === 'Problème d\'accès' && (
          <>
            <SectionTitle color="#D42B2B">Photos du blocage</SectionTitle>
            <PhotoSection label="Photos du blocage (sans limite)" photos={photosCheminementFailed} onChange={setPhotosCheminementFailed} max={0} />
            <Textarea label="Description du problème d'accès" value={descBlockage} onChange={setDescBlockage} placeholder="Décrire le problème d'accès..." />
          </>
        )}

        {/* FAILED — Commande annulé par le client */}
        {isFailed && isCommandeAnnulee && (
          <>
            <SectionTitle color="#D42B2B">Annulation client</SectionTitle>
            <Textarea label="Raison de l'annulation client" value={raisonAnnulationClient} onChange={setRaisonAnnulationClient} placeholder="Expliquer la raison de l'annulation par le client..." />
            <PhotoSection label="Photos justificatif(s) de l'annulation (facultatif)" photos={photosAnnulationClient} onChange={setPhotosAnnulationClient} max={0} />
          </>
        )}

        {/* FAILED — Chambre sur voirie besoin ARC */}
        {isFailed && isChambreVoirieARC && (
          <>
            <SectionTitle color="#D42B2B">Chambre sur voirie — ARC</SectionTitle>
            <PhotoSection label="Photo de la chambre dans son environnement" hint="Permettant de situer la chambre (chaussée, trottoir...)" photos={photoChambreVoirieEnv} onChange={setPhotoChambreVoirieEnv} max={3} />
            <PhotoSection label="Photos de la chambre vue du dessus" photos={photosChambreVoirieDessus} onChange={setPhotosChambreVoirieDessus} max={0} />
            <GPSButton label="Emplacement GPS de la chambre" value={gpsChambreVoirie} onChange={setGpsChambreVoirie} />
            <Input label="Adresse de la chambre ARC" value={adresseChambreVoirie} onChange={setAdresseChambreVoirie} placeholder="Ex: devant le 12 rue de la Paix, 75001 Paris" />
            <Textarea label="Commentaire lié à l'intervention" value={commentaireChambreVoirie} onChange={setCommentaireChambreVoirie} placeholder="Décrire le contexte de l'intervention..." />
          </>
        )}

        {/* FAILED — Pas de continuité PB PM */}
        {isFailed && typeEchec === 'Pas de continuité PB PM' && (
          <>
            <SectionTitle color="#D42B2B">Pas de continuité PB PM</SectionTitle>
            <PhotoSection label="Photo Problème continuité PBO" photos={photoContinuitePBO} onChange={setPhotoContinuitePBO} max={0} />
            <PhotoSection label="Photo signal PM" photos={photoSignalPM} onChange={setPhotoSignalPM} max={0} />
            <Textarea label="Description de l'échec" value={descBlockage} onChange={setDescBlockage} placeholder="Décrire le problème de continuité..." />
          </>
        )}

        {/* FAILED — Pas de signal PM */}
        {isFailed && typeEchec === 'Pas de signal PM' && (
          <>
            <SectionTitle color="#D42B2B">Pas de signal PM</SectionTitle>
            <PhotoSection label="Photo signal coupleur PM" photos={photoSignalCoupleurPM} onChange={setPhotoSignalCoupleurPM} max={0} />
            <Textarea label="Description de l'échec" value={descBlockage} onChange={setDescBlockage} placeholder="Décrire le problème de signal PM..." />
          </>
        )}

        {/* FAILED — Erreur d'adresse */}
        {isFailed && typeEchec === 'Erreur d\'adresse' && (
          <>
            <SectionTitle color="#D42B2B">Erreur d'adresse</SectionTitle>
            <PhotoSection label="Photos adresse client" photos={photosAdresseClient} onChange={setPhotosAdresseClient} max={0} />
            <GPSButton label="Coordonnée adresse client" value={gpsAdresseClient} onChange={setGpsAdresseClient} />
            <Textarea label="Description de l'échec" value={descBlockage} onChange={setDescBlockage} placeholder="Décrire l'erreur d'adresse..." />
          </>
        )}

        {/* FAILED — Client non éligible FTTH / Autre */}
        {isFailed && (typeEchec === 'Client non éligible FTTH' || typeEchec === 'Autre') && (
          <>
            <SectionTitle color="#D42B2B">Photos de l'échec</SectionTitle>
            <PhotoSection label="Photo dû à l'échec (sans limite)" photos={photosEchec} onChange={setPhotosEchec} max={0} />
            <Textarea label="Description de l'échec" value={descBlockage} onChange={setDescBlockage} placeholder="Expliquer la raison de l'échec..." />
          </>
        )}

        {/* FAILED — PBO Saturé */}
        {isFailed && isPBOSature && (
          <>
            <SectionTitle color="#D42B2B">PBO Saturé</SectionTitle>
            <PhotoSection label="Photo PBO Saturé" photos={photoPBOSature} onChange={setPhotoPBOSature} max={0} />
            <Toggle label="Autre PBO disponible" value={autrePBODisponible} onChange={setAutrePBODisponible} />
            {autrePBODisponible && (
              <>
                <Input label="Référence du PBO disponible" value={refPBODisponible} onChange={setRefPBODisponible} placeholder="Ex: PBO-XXX-XXX" />
                <PhotoSection label="Photo du PBO disponible" photos={photosPBODisponible} onChange={setPhotosPBODisponible} max={0} />
                <Input label="Adresse du PBO disponible" value={adressePBODisponible} onChange={setAdressePBODisponible} placeholder="Ex: 12 rue de la Paix, 75001 Paris" />
                <GPSButton label="Coordonnées GPS du PBO disponible" value={gpsPBODisponible} onChange={setGpsPBODisponible} />
              </>
            )}
            <Toggle label="Arrive-t-on sur une autre PM ?" value={arriveeMemePM} onChange={setArriveeMemePM} />
            {arriveeMemePM && (
              <Input label="Référence de la nouvelle PM" value={refNouvellePM} onChange={setRefNouvellePM} placeholder="Ex: PM-XXX-XXX" />
            )}
          </>
        )}

        {/* FAILED — ARC (commun à Blocage GC, Devis complexe, Chambre inaccessible, Autre) */}
        {isFailed && isARC && (
          <>
            <Toggle label="Besoin d'un ARC (Arrêté de circulation)" value={besoinARC} onChange={setBesoinARC} />
            {besoinARC && (
              <>
                <PhotoSection label="Photos chambre concernée pour ARC" photos={photosARC} onChange={setPhotosARC} max={0} />
                <GPSButton label="Position GPS de la chambre (ARC)" value={gpsARC} onChange={setGpsARC} />
                <Input label="Adresse de la chambre ARC" value={adresseARC} onChange={setAdresseARC} placeholder="Ex: devant le 12 rue de la Paix, 75001 Paris" />
              </>
            )}
          </>
        )}

        {/* ========== CLEARED ========== */}
        {isCleared && (
          <>
            <SectionTitle>Détails du raccordement</SectionTitle>
            <div className="field">
              <div className="field-label">Type de raccordement réalisé</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {TYPES_RACCORDEMENT.map(t => (
                  <div key={t} onClick={() => setTypeRacc(t)} style={{ padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${typeRacc === t ? 'var(--accent)' : 'var(--bord-fort)'}`, background: typeRacc === t ? 'var(--info-fond)' : 'var(--surface-2)', color: typeRacc === t ? 'var(--info-texte)' : 'var(--texte-doux)', fontWeight: typeRacc === t ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>{t}</div>
                ))}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Installation / utilisation PTO</div>
              <select value={usagePTO} onChange={e => { setUsagePTO(e.target.value); setUsagePTOManuallySet(true); }} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }}>
                <option>Installation d'une nouvelle prise optique</option>
                <option>Utilisation d'une prise existante</option>
              </select>
            </div>
            {typeRacc !== 'PTO_C' && <>
              <Input label="Longueur câble domaine public (ml)" value={lgPublic} onChange={setLgPublic} type="number" />
              <Input label="Longueur câble domaine privé (ml)" value={lgPrive} onChange={setLgPrive} type="number" />
              <InfoCard><strong>Longueur totale calculée : {lgTotal} ml</strong></InfoCard>
              <Input label="Longueur passage faux plafond (ml)" value={lgFauxPlafond} onChange={setLgFauxPlafond} type="number" />
              <Toggle label="FPV (forfait plus-value)" value={fpv} onChange={setFpv} />
              <Toggle label="Percement(s) complexe(s) effectué(s)" value={percement} onChange={setPercement} />
              {percement && (
                <>
                  <Input label="Nombre de percements complexes" value={nbPercementsComplexes} onChange={setNbPercementsComplexes} type="number" />
                  <PhotoSection label="Photos percement(s) complexe(s)" photos={photosPercementsComplexes} onChange={setPhotosPercementsComplexes} max={0} />
                  <Input label="Nom du signataire de l'autorisation de percement" value={nomSignatairePercement} onChange={setNomSignatairePercement} />
                  <div className="field">
                    <div className="field-label">Signature autorisation de percement</div>
                    {signedPercement ? (
                      <div>
                        <div style={{ border: '0.5px solid var(--succes-bord)', borderRadius: 10, background: 'var(--succes-fond)', padding: 8 }}><img src={signaturePercement} alt="signature percement" style={{ maxHeight: 80 }} /></div>
                        <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => { sigPercRef.current?.clear(); setSignaturePercement(''); setSignedPercement(false); }}>Effacer</div>
                      </div>
                    ) : (
                      <div>
                        <div className="sign-canvas-wrap"><SignatureCanvas ref={sigPercRef} canvasProps={{ width: 340, height: 100, style: { display: 'block', width: '100%', height: 100 } }} onEnd={captureSignaturePercement} /></div>
                        <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => sigPercRef.current?.clear()}>Effacer</div>
                      </div>
                    )}
                  </div>
                </>
              )}
              <Toggle label="Hydrocurage effectué" value={hydrocurage} onChange={setHydrocurage} />
            </>}

            {/* Cheminement — masqué pour PTO_C */}
            {typeRacc !== 'PTO_C' && <>
              <SectionTitle>Cheminement PBO → Adduction → PTO</SectionTitle>
              <PhotoSection label="Photos cheminement (sans limite)" photos={photosCheminement} onChange={setPhotosCheminement} max={0} />
            </>}

            {/* PTO */}
            <SectionTitle>PTO — Point de terminaison</SectionTitle>
            <Toggle label="ROP respectée" value={ropRespectee} onChange={setRopRespectee} />
            {!ropRespectee && (
              <div style={{ background: 'var(--attention-fond)', border: '1px solid var(--attention-bord)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--attention-texte)', marginBottom: 8 }}>Nouvelle route optique :</div>
                <Input label="Nouvelle Réf PBO" value={nvRefPBO} onChange={setNvRefPBO} />
                <Input label="Nouveau Tube" value={nvTubePBO} onChange={setNvTubePBO} />
                <Input label="Nouvelle Fibre" value={nvFibrePBO} onChange={setNvFibrePBO} />
                <div className="field">
                  <div className="field-label" style={{ color: 'var(--danger-texte)' }}>Décharge OI <span style={{ color: 'var(--danger-texte)' }}>*</span></div>
                  <input type="text" value={dechargeOi} onChange={e => setDechargeOi(e.target.value)} placeholder="N° de décharge OI (obligatoire)" style={{ width: '100%', padding: '10px 12px', border: `0.5px solid ${!dechargeOi ? 'var(--danger)' : 'var(--bord-fort)'}`, borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
                  {!dechargeOi && <div style={{ fontSize: 11, color: 'var(--danger-texte)', marginTop: 3 }}>Obligatoire si ROP non respectée</div>}
                </div>
              </div>
            )}
            <div className="field">
              <div className="field-label">Réf PTO (modifiable)</div>
              <input type="text" value={refPTOModifiee} onChange={e => setRefPTOModifiee(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: `0.5px solid ${refPTOModifiee === 'HOTLINE' ? 'var(--attention-bord)' : 'var(--bord-fort)'}`, borderRadius: 10, fontSize: 14, background: refPTOModifiee === 'HOTLINE' ? 'var(--attention-fond)' : 'var(--surface-2)', color: 'var(--texte)' }} />
              {refPTOModifiee === 'HOTLINE' && <div style={{ fontSize: 12, color: 'var(--danger-texte)', marginTop: 4, fontWeight: 600 }}>⚠️ Référence HOTLINE — remplacer par la référence transmise par l'OI lors de l'appel au PPC.</div>}
            </div>
            <PhotoSection label="Photo référence PTO" photos={photoRefPTO} onChange={setPhotoRefPTO} max={2} />
            <PhotoSection label="Photo valeur photomètre PTO" photos={photoPhotometrePTO} onChange={setPhotoPhotometrePTO} max={2} />

            {/* PBO */}
            <SectionTitle>PBO — Point de branchement opérateur</SectionTitle>
            {(dossier?.ref_pt_pbo || dossier?.tube || dossier?.fibre) && (
              <div style={{ background: 'var(--info-fond)', border: '1px solid var(--info-bord)', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13 }}>
                {dossier.ref_pt_pbo && <div><span style={{ fontWeight: 600 }}>Réf PT PBO :</span> {dossier.ref_pt_pbo}</div>}
                {dossier.tube && <div><span style={{ fontWeight: 600 }}>Tube :</span> {dossier.tube}</div>}
                {dossier.fibre && <div><span style={{ fontWeight: 600 }}>Fibre :</span> {dossier.fibre}</div>}
              </div>
            )}
            <PhotoSection label="Photo PBO ouvert — AVANT" photos={photoPBOAvant} onChange={setPhotoPBOAvant} max={3} />
            <PhotoSection label="Photo PBO ouvert — APRÈS" photos={photoPBOApres} onChange={setPhotoPBOApres} max={3} />
            <PhotoSection label="Photo référence FI sur câble PBO" photos={photoRefFiCablePBO} onChange={setPhotoRefFiCablePBO} max={2} />

            {/* PM */}
            <SectionTitle>PM — Point de mutualisation</SectionTitle>
            {(dossier?.n_pt_pm || dossier?.position_bloc_operateur || dossier?.position_bloc_client) && (
              <div style={{ background: 'var(--info-fond)', border: '1px solid var(--info-bord)', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13 }}>
                {dossier.n_pt_pm && <div><span style={{ fontWeight: 600 }}>N° PT PM :</span> {dossier.n_pt_pm}</div>}
                {dossier.adresse_pm && <div><span style={{ fontWeight: 600 }}>Adresse PM :</span> {dossier.adresse_pm}</div>}
                {dossier.position_bloc_operateur && <div><span style={{ fontWeight: 600 }}>Position bloc opérateur :</span> {dossier.position_bloc_operateur}</div>}
                {dossier.position_bloc_client && <div><span style={{ fontWeight: 600 }}>Position bloc client :</span> {dossier.position_bloc_client}</div>}
              </div>
            )}
            <Toggle label="Position respectée au PM" value={positionRespecteepm} onChange={setPositionRespecteepm} />
            {!positionRespecteepm && (
              <div style={{ background: 'var(--attention-fond)', border: '1px solid var(--attention-bord)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--attention-texte)', marginBottom: 8 }}>Nouvelle position PM :</div>
                <Input label="Position Opérateur porte gauche" value={nvPositionOperateurPM} onChange={setNvPositionOperateurPM} placeholder="Ex: Slot#01/E02" />
                <Input label="Position Client porte droite" value={nvPositionClientPM} onChange={setNvPositionClientPM} placeholder="Ex: Slot#04/A04" />
                <Input label="N° de décharge OI" value={dechargeOiPM} onChange={setDechargeOiPM} placeholder="N° de décharge OI" />
              </div>
            )}
            <PhotoSection label="Photo PM — AVANT" photos={photoPMAvant} onChange={setPhotoPMAvant} max={3} />
            <PhotoSection label="Photo PM — APRÈS" photos={photoPMApres} onChange={setPhotoPMApres} max={3} />
            <PhotoSection label="Photo référence FI sur jarretière au PM" photos={photoRefFiJarrPM} onChange={setPhotoRefFiJarrPM} max={2} />
            <PhotoSection label="Photo valeur photomètre PM" photos={photoPhotometrePM} onChange={setPhotoPhotometrePM} max={2} />

            {/* ONT */}
            <SectionTitle>ONT — Optical Network Terminal</SectionTitle>
            <div className="field">
              <div className="field-label">Constructeur ONT</div>
              <select value={constructeurOnt} onChange={e => setConstructeurOnt(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: constructeurOnt ? 'var(--texte)' : 'var(--texte-doux)' }}>
                <option value="">— Sélectionner —</option>
                <option value="NOKIA">NOKIA</option>
                <option value="Huawei">Huawei</option>
                <option value="ZTE">ZTE</option>
              </select>
            </div>
            <InfoCard>
              <div><strong>SLID offre active :</strong> {dossier.slid_active || '—'}</div>
              <div><strong>SLID offre passive :</strong> {dossier.slid_passive || '—'}</div>
            </InfoCard>
            <div className="field">
              <div className="field-label">N° de série ONT</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={numSerieOnt}
                  onChange={e => setNumSerieOnt(e.target.value)}
                  placeholder="Ex: ALCLFD574109"
                  autoCapitalize="characters"
                  style={{ flex: 1, padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }}
                />
                <button
                  onClick={() => setShowScanner(true)}
                  style={{ padding: '10px 14px', background: 'var(--accent-plein)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  📷 Scanner
                </button>
              </div>
              {numSerieOnt && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--succes-texte)', background: 'var(--succes-fond)', padding: '4px 10px', borderRadius: 6 }}>
                  ✓ {numSerieOnt}
                </div>
              )}
            </div>
            {showScanner && (
              <BarcodeScanner
                onDetected={val => { setNumSerieOnt(val); setShowScanner(false); }}
                onClose={() => setShowScanner(false)}
              />
            )}
            <PhotoSection label="Photo étiquette S/N ONT" photos={photoEtiquetteONT} onChange={setPhotoEtiquetteONT} max={2} />
            <PhotoSection label="Photo ONT voyant fibre VERT" photos={photoVoyantONT} onChange={setPhotoVoyantONT} max={2} />
            <PhotoSection label="Photo test PPP (PC)" photos={photoTestPPP} onChange={setPhotoTestPPP} max={2} />
            {dossier.n_via?.startsWith('VIA00') && (
              <div className="field">
                <div className="field-label">Test activation FTTH</div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleViaTest}
                  disabled={viaTestLoading}
                  style={{ width: '100%', padding: '12px', fontSize: 15, opacity: viaTestLoading ? 0.6 : 1 }}
                >
                  {viaTestLoading ? '⏳ Test en cours… (environ 40s)' : '🔧 Lancer le test VIA'}
                </button>
                {viaTestError && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--danger-fond)', border: '1px solid var(--danger-bord)', borderRadius: 8, fontSize: 13, color: 'var(--danger-texte)' }}>
                    ❌ {viaTestError}
                  </div>
                )}
              </div>
            )}
            {dossier.n_via?.startsWith('VIA00') && (
              <PhotoSection label="Photo test activation FTTH" photos={photoTestActivation} onChange={setPhotoTestActivation} max={2} />
            )}
            <Toggle label="CPE installé" value={cpeInstalle} onChange={setCpeInstalle} />
          </>
        )}

        {/* CLOTURE */}
        <SectionTitle>Clôture de l'intervention</SectionTitle>
        <div className="field">
          <div className="field-label">Date et heure d'arrivée sur site</div>
          <input type="datetime-local" value={dateArrivee} onChange={e => setDateArrivee(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
        </div>
        <div className="field">
          <div className="field-label">Date et heure de fin d'intervention</div>
          <input type="datetime-local" value={dateFin} onChange={e => setDateFin(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
        </div>
        <Textarea label="Commentaire lié à l'intervention" value={commentaireIntervention} onChange={setCommentaireIntervention} placeholder={isCleared ? "Décrire ce qui a été réalisé lors de l'intervention..." : "Décrire la raison de l'échec..."} />
        {isFailed && (isContinuitePBPM || isSignalPM || isErreurAdresse || isPBOSature) && (
          <div className="field">
            <div className="field-label">N° de décharge OI</div>
            <input type="text" value={noDechargeOI} onChange={e => setNoDechargeOI(e.target.value)} placeholder="N° de décharge OI (facultatif)"
              style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
          </div>
        )}
        {isFailed && (
        <div className="field">
          <div className="field-label">N° de décharge COVAGE <span style={{ color: 'var(--danger)' }}>*</span></div>
          <input type="text" value={noDecharge} onChange={e => setNoDecharge(e.target.value)} placeholder="Ex: D260401_15424"
            style={{ width: '100%', padding: '10px 12px', border: `0.5px solid ${noDecharge ? 'var(--succes-bord)' : 'var(--danger)'}`, borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
          {!noDecharge && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>Obligatoire pour un résultat FAILED</div>}
        </div>
      )}
        <Input label="Nom du contact sur site" value={nomContact} onChange={setNomContact} />
        <Input label="Email du contact sur site" value={emailContact} onChange={setEmailContact} />

        <div className="field">
          <div className="field-label">Signature du contact sur site</div>
          {signedSite ? (
            <div>
              <div style={{ border: '0.5px solid var(--succes-bord)', borderRadius: 10, background: 'var(--succes-fond)', padding: 8 }}><img src={signatureSite} alt="signature" style={{ maxHeight: 80 }} /></div>
              <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => { sigRef.current?.clear(); setSignatureSite(''); setSignedSite(false); }}>Effacer et recommencer</div>
            </div>
          ) : (
            <div>
              <div className="sign-canvas-wrap"><SignatureCanvas ref={sigRef} canvasProps={{ width: 340, height: 120, style: { display: 'block', width: '100%', height: 120 } }} onEnd={captureSignatureSite} /></div>
              <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => sigRef.current?.clear()}>Effacer</div>
            </div>
          )}
        </div>

        {message && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: message.includes('succès') || message.includes('sauvegardé') ? 'var(--succes-fond)' : 'var(--danger-fond)', color: message.includes('succès') || message.includes('sauvegardé') ? 'var(--succes-texte)' : 'var(--danger-texte)', fontSize: 13, marginBottom: 10 }}>
            {message}
          </div>
        )}
        {pdfUrl && (
          <button onClick={() => {
            const token = localStorage.getItem('token');
            fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob())
              .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = pdfUrl.split('/').pop() || 'CR.pdf';
                a.click();
                URL.revokeObjectURL(url);
              });
          }} style={{ display: 'block', width: '100%', textAlign: 'center', padding: '11px', borderRadius: 12, background: 'var(--surface-3)', color: 'var(--texte)', border: '0.5px solid var(--bord-fort)', fontWeight: 600, marginBottom: 8, fontSize: 15, cursor: 'pointer' }}>
            Télécharger le PDF
          </button>
        )}
        <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>{generating ? 'Génération PDF…' : `Générer CRI ${resultat}`}</button>
        <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>{saving ? 'Sauvegarde…' : 'Sauvegarder brouillon'}</button>
        <div style={{ height: 16 }} />
      </div>

      <div className="bottomnav">
        <div className="nav-item" onClick={() => navigate('/planning')}><span className="nav-icon">📅</span>Planning</div>
        <div className="nav-item" onClick={handleBack}><span className="nav-icon">📁</span>Dossier</div>
        <div className="nav-item active"><span className="nav-icon">📝</span>CR</div>
      </div>
    </div>
  );
}
